import React, { ReactNode, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { createPortal } from 'react-dom'

import { IconClose } from 'components/Icons/IconClose'
import { Typography } from 'components/Typography/Typography'

interface PopupDialogProps {
  title: ReactNode
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  id?: string
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${p => p.theme.colors.dialog.overlay};
  z-index: 999;
`

const DialogContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 24px;
  z-index: 1000;
  width: 689px;
  max-height: 90vh;
  border-radius: 16px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  box-shadow: 0px 4px 50px 0px ${p => p.theme.colors.background.hover};
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 85%;
    max-height: 90vh;
    padding: 16px;
  }
`

const DialogTitle = styled.div`
  position: relative;
  text-align: center;
  border-bottom: 1px solid ${p => p.theme.colors.dialog.titleBorder};
  padding-bottom: 16px;

  & > div > svg {
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 4px;
  }
`

const DialogContent = styled.div`
  flex: 1;
  overflow-y: auto;
`

export const Dialog: React.FC<PopupDialogProps> = ({ title, isOpen, onClose, children, id }) => {
  const dialogNode = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (dialogNode.current && !dialogNode.current.contains(e.target as Node)) {
      onClose()
    }
  }

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return createPortal(
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <DialogContainer ref={dialogNode} onClick={stopPropagation}>
        <DialogTitle>
          <Typography variant="label1" fontWeight="medium">
            {title}
          </Typography>
          <div onClick={onClose}>
            <IconClose />
          </div>
        </DialogTitle>
        <DialogContent id={id}>{children}</DialogContent>
      </DialogContainer>
    </Overlay>,
    document.body
  )
}
